import { useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import LoginPage from "./Pages/LoginPage";
import UnityViewer from "./Pages/UnityViewer";
import SandboxesAdminPage from "./Pages/Admin/Sandboxes/SandboxesAdminPage";
import RBAC from "./Components/RBAC";
import { Roles } from "./Constants/BorgEnums";
import Error from "./Pages/ErrorPages/Error";
import AdminPageContainer from "./Components/Admin/AdminPageContainer";
import AdminDashboardPage from "./Pages/Admin/AdminDashboardPage";
import UsersAdminPage from "./Pages/Admin/Users/UsersAdminPage";
import OrganizationsAdminPage from "./Pages/Admin/OrganizationsAdminPage";
import GroupsAdminPage from "./Pages/Admin/GroupsAdminPage";
import GradebookPage from "./Pages/Admin/GradebookPage";
import LeaderboardsAdminPage from "./Pages/Admin/LeaderboardsAdminPage";
import PermissionsAdminPage from "./Pages/Admin/PermissionsAdminPage";
import { AxiosInterceptorSetup } from "./Services/AxiosService";
import { ThemeProvider, CssBaseline, AlertColor } from "@mui/material";
import BorgTheme from "./GlobalStyles/MuiTheme";
import SuccessfulPayment from "./Pages/SuccessfulPayment";
import FailedPayment from "./Pages/FailedPayment";
import RegisterPage from "./Pages/RegisterPage";
import PasswordResetPage from "./Pages/PasswordResetPage";
import SystemAdminPage from "./Pages/Admin/SystemAdminPage";
import BannerMessage from "./Components/BannerMessage";
import InstructorDashboardPage from "./Pages/Admin/InstructorDashboardPage";
import { getBannerMessage } from "./Services/BorgAPIClient";
import { CustomAlert, CustomSnackbar } from "./Components/CustomSnackbar";
import QuizPage from "./Pages/Admin/QuizPage";
import ResetPasswordUser from "./Components/ResetPasswordUser";
import MainPageTemplate from "./Pages/Templates/MainPageTemplate";

type BannerMessageType = {
  startDate: string;
  endDate: string;
  message: string;
};

interface Props { }

function RouterComp(props: Props) {

  const navigate = useNavigate();
  const location = useLocation();

  const [isLoaded, setIsLoaded] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<BannerMessageType | null>(null);
  const [serverBannerMessageIsValid, setServerBannerMessageIsValid] = useState<boolean>(false); // by default, server message is not valid
  const [notification, setNotification] = useState<string | null>(null);
  const [notificationType, setNotificationType] = useState<AlertColor>("info");

  const unityViewerRef = useRef<any>(null);

  const isKepleraVersion = process.env.REACT_APP_PRODUCT_NAME === "keplera";
  const productName = isKepleraVersion ? "Keplera" : "BORG Space"

  const buildBannerKey = (banner: BannerMessageType) => {
    return `${banner.startDate}|${banner.endDate}|${banner.message}`;
  };

  const fetchBannerMessage = useCallback(async () => {
    let shouldShowBanner = false;

    try {
      const result = await getBannerMessage();
      const bannerMessageData = result.data as BannerMessageType;

      const isWithinDateRange = isWithinBannerDateRange(
        new Date(bannerMessageData.startDate),
        new Date(bannerMessageData.endDate)
      );

      const storedDismissedKey = localStorage.getItem("dismissedBannerKey");
      const currentBannerKey = buildBannerKey(bannerMessageData);

      // show the banner if it's within the effective date range AND
      // the user has not already dismissed it.
      shouldShowBanner = isWithinDateRange && storedDismissedKey !== currentBannerKey;

      setBannerMessage(bannerMessageData);
      setServerBannerMessageIsValid(shouldShowBanner);
      sessionStorage.setItem("showBanner", shouldShowBanner ? "true" : "false");
    } catch (error) {
      console.error('Failed to fetch Banner Message:', error);
      sessionStorage.setItem("showBanner", "false");
      setServerBannerMessageIsValid(false);
    }

    console.log(`Show Banner: ${shouldShowBanner}`);
  }, []);

  useEffect(() => {
    fetchBannerMessage();
    document.title = location.pathname.includes("/admin") ? productName + " - Admin Panel" : productName;
  }, [fetchBannerMessage, location.pathname, productName]);

  const isWithinBannerDateRange = (bannerStartDate: Date, bannerEndDate: Date) => {
    // Check if either start or end date is null
    if (!bannerStartDate || !bannerEndDate) {
      return false; // Return false if either date is null
    }

    const currentDate = new Date();
    const showBanner = currentDate >= bannerStartDate && currentDate <= bannerEndDate;

    return showBanner;
  };

  const onCloseBanner = () => {
    if (bannerMessage) {
      const currentBannerKey = buildBannerKey(bannerMessage);
      localStorage.setItem("dismissedBannerKey", currentBannerKey);
    }

    setServerBannerMessageIsValid(false);
    sessionStorage.setItem("showBanner", "false");
    unityViewerRef.current?.handleResize();
    console.log("Show Banner: false");
  };

  const onNotification = (message: string, notificationType?: AlertColor) => {
    setNotification(message);
    setNotificationType(notificationType ? notificationType : "info");
  }

  const getRoutes = () => {
    return (
      <Routes>
        <Route element={<MainPageTemplate />}>
          <Route path="/" element={<LoginPage onNotification={onNotification}/>} />
          <Route path="/reset-password" element={<ResetPasswordUser/>} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
        </Route>
        <Route path="/play" element={<UnityViewer ref={unityViewerRef}/>} />
        <Route element={<RBAC allowedRole={[Roles.SystemAdmin, Roles.OrganizationAdmin]} />}>
          <Route path="/admin" element={<AdminDashboardPage/>} />
          <Route path="/admin/users" element={<UsersAdminPage onNotification={onNotification}/>} />
          <Route path="/admin/organizations" element={<OrganizationsAdminPage />} />
          <Route path="/admin/courses" element={<GroupsAdminPage onNotification={onNotification}/>} />
          <Route path="/admin/sandboxes" element={<SandboxesAdminPage onNotification={onNotification}/>} />
          <Route path="/admin/leaderboards" element={<LeaderboardsAdminPage />} />
          <Route path="/admin/permissions" element={<PermissionsAdminPage />} />
          <Route path="/admin/system" element={<SystemAdminPage />} />
        </Route>
        <Route element={<RBAC allowedRole={[Roles.SystemAdmin, Roles.OrganizationAdmin, Roles.Instructor ]} />}>
          <Route path="/instructor" element={<InstructorDashboardPage />} />
          <Route path="/instructor/courses" element={<GroupsAdminPage onNotification={onNotification}/>} />
          <Route path="/instructor/gradebook/:sessionId" element={<GradebookPage onNotification={onNotification}/>} />
          <Route path="/gradebook/:sessionId" element={<GradebookPage onNotification={onNotification}/>} />
          <Route path="/instructor/gradebook/:sessionId/quiz/:scenarioId" element={<QuizPage onNotification={onNotification}/>} />
          <Route path="/gradebook/:sessionId/quiz/:scenarioId" element={<QuizPage onNotification={onNotification}/>} />
        </Route>
        <Route path="/success" element={<SuccessfulPayment />} />
        <Route path="/failed" element={<FailedPayment />} />
        <Route path="/forbidden" element={
          <Error
            errorCode={403}
            errorText={"Access Denied (Forbidden)"}
            errorHelp={"You do not have permission to access this resource"}
          />}
        />
        <Route path="/server-error" element={
          <Error
            errorCode={500}
            errorText={"Unexpected server error!"}
            errorHelp={"If problem persists contact support"}
          />}
        />
        <Route path="*" element={
          <Error
            errorCode={404}
            errorText={"Page not found!"}
            errorHelp={"The resource you are looking for does not exist."}
          />}
        />
      </Routes>
    );
  }

  if (!isLoaded) {
    setIsLoaded(true)
    AxiosInterceptorSetup(navigate);
  }

  let content;
  if (location.pathname.includes("/admin") || location.pathname.includes("/instructor")) {
    content = (
      <AdminPageContainer>
        {getRoutes()}
      </AdminPageContainer>
    );
  } else {
    content = (
      <div>
        <div>
          {getRoutes()}
        </div>
      </div>
    );
  }
  return (
    <ThemeProvider theme={BorgTheme}>
      <CssBaseline />
      {/* Only show the BannerMessage if the current date and time is within the banner date range and the banner has not been closed by the user */}
      <BannerMessage visible={serverBannerMessageIsValid} message={bannerMessage?.message} onCloseBanner={onCloseBanner} />
      {content}
      <CustomSnackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => { setNotification(null); }}
      >
        <CustomAlert onClose={() => setNotification(null)} severity={notificationType}>
          {notification}
        </CustomAlert>
      </CustomSnackbar>
    </ThemeProvider>
  );
}

export default RouterComp;
